import React, {
  MouseEvent,
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
} from 'react';
import Modal from '../../../components/modals/Modal';
import { Brand, BrandCategory } from '../../../fetch/fetchBrands';
import { StoreSettings } from '../storeSettings/storeTypes';
import Fetch from '../../../utils/fetch';
import { settings } from '../../../settings';
import { fetchAllStoresInfo } from '../../../fetch/fetchStore';
import { classNames } from '../../../utils/classUtils';
import './BrandFormModal.scss';
import { CheckboxComponent } from '../../../ReusableComponents/Checkbox/CheckboxComponent';

import { useDropzone } from 'react-dropzone';

interface ErrorMsg {
  message: string;
}
type BrandFields = 'accountId' | 'name' | 'url';

type ErrorMessages = {
  [field in BrandFields]?: ErrorMsg;
};

const initValueForBrand: Brand = {
  id: undefined,
  accountId: -1,
  name: '',
  url: '',
  logoUrl: '',
  categories: [],
  hidden: false,
};

interface CreateBrandRequest {
  accountId: number;
  name: string;
  url: string;
  categories: BrandCategory[];
  hidden: boolean;
}

interface UpdateBrandRequest {
  id: number;
  accountId: number;
  name: string;
  url: string;
  categories: BrandCategory[];
  hidden: boolean;
}

interface CreateResult {
  success: boolean;
  errors: ErrorMsg[];
}

export const BrandFormModal = (props: {
  onCloseModal: (created: boolean) => void;
  brand?: Brand;
}) => {
  const [loading, setLoading] = useState(false);
  const [creatingBrand, setCreatingBrand] = useState<boolean>(false);
  const [brand, setBrand] = useState<Brand>(props.brand || initValueForBrand);
  const [brandCreated, setBrandCreated] = useState<boolean>(false);
  const [storeSettings, setStoreSettings] = useState<StoreSettings[]>([]);
  const [fieldErrors, setFieldErrors] = useState<ErrorMessages>({});
  const [errors, setErrors] = useState<ErrorMsg[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    Set<BrandCategory>
  >(props.brand ? new Set(props.brand.categories) : new Set());
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [logo, setLogo] = useState(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: files => {
      setLogo(
        files.map(file => {
          return {
            file,
            previewUrl: URL.createObjectURL(file),
          };
        })
      );
    },
  });

  useEffect(() => {
    const escFunction = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        props.onCloseModal(false);
      }
    };

    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [props]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const stores = await fetchAllStoresInfo();
      setStoreSettings(stores);
      setLoading(false);
    })();
  }, []);

  const validateFields = (brand: Brand): ErrorMessages => {
    const fieldErrors: ErrorMessages = {};
    fieldErrors.name = validateName(brand) || undefined;
    fieldErrors.accountId = validateAccountId(brand) || undefined;
    fieldErrors.url = validateUrl(brand) || undefined;

    return fieldErrors;
  };

  const hasErrors = (fieldErrors: ErrorMessages): boolean => {
    return Object.values(fieldErrors).filter(err => !!err).length > 0;
  };

  const setBrandField = (updatedBrandField: Partial<Brand>) => {
    const updatedBrand = {
      ...brand,
      ...updatedBrandField,
    };
    setBrand(updatedBrand);
    setBrandCreated(false);

    if (hasErrors(fieldErrors)) {
      const fieldErrors = validateFields(updatedBrand);
      setFieldErrors(fieldErrors);
    }
  };

  const handleCategoryCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const categoryName = event.currentTarget.name as BrandCategory;
      const updatedCategories = new Set(selectedCategories);
      if (!selectedCategories.has(categoryName)) {
        updatedCategories.add(categoryName);
      } else {
        updatedCategories.delete(categoryName);
      }
      setSelectedCategories(updatedCategories);
    },
    [selectedCategories]
  );

  async function updateBrand(brand: UpdateBrandRequest): Promise<CreateResult> {
    try {
      const res = await Fetch.put({
        url: `${settings.newApiUrl}/brands/${brand.id}`,
        body: brand,
      });
      if (logo && res.ok) {
        const logoRes = await Fetch.postImage({
          url: `${settings.newApiUrl}/brands/${brand.id}/logo-upload`,
          file: logo[0].file,
        });
        if (!logoRes.ok) {
          return {
            success: false,
            errors: [
              {
                message: 'Brand was updated, but failed to upload brand logo.',
              },
            ],
          };
        }
      }
      if (res.ok) {
        return { success: true, errors: [] };
      } else {
        return {
          success: false,
          errors: [
            {
              message: 'Something went wrong. The brand could not be updated.',
            },
          ],
        };
      }
    } catch (e) {
      return {
        success: false,
        errors: [
          { message: 'Something went wrong. The brand could not be updated.' },
        ],
      };
    }
  }

  async function createBrand(brand: CreateBrandRequest): Promise<CreateResult> {
    try {
      const res = await Fetch.post({
        url: `${settings.newApiUrl}/brands`,
        body: brand,
      });
      if (logo && res.ok) {
        const saveResponse = await res.json();
        const logoRes = await Fetch.postImage({
          url: `${settings.newApiUrl}/brands/${saveResponse.id}/logo-upload`,
          file: logo[0].file,
        });
        if (!logoRes.ok) {
          return {
            success: false,
            errors: [
              {
                message: 'Brand was created, but failed to upload logo.',
              },
            ],
          };
        }
      }
      if (res.ok) {
        return { success: true, errors: [] };
      } else {
        return {
          success: false,
          errors: [
            {
              message: 'Something went wrong. The brand could not be created.',
            },
          ],
        };
      }
    } catch (e) {
      return {
        success: false,
        errors: [
          { message: 'Something went wrong. The brand could not be created.' },
        ],
      };
    }
  }

  async function saveBrand(e: MouseEvent) {
    e.preventDefault();
    const fieldErrors = validateFields(brand);
    setFieldErrors(fieldErrors);
    setBrandCreated(false);

    if (hasErrors(fieldErrors)) {
      return;
    }

    setErrors([]);
    setCreatingBrand(true);
    let result = null;

    const brandObject = {
      accountId: brand.accountId,
      name: brand.name,
      categories: Array.from(selectedCategories.values()),
      url: brand.url,
      hidden: isHidden,
    };

    if (props.brand) {
      result = await updateBrand({
        ...brandObject,
        id: brand.id,
      });
    } else {
      result = await createBrand(brandObject);
    }

    setCreatingBrand(false);
    if (result.success) {
      setBrandCreated(true);
      setErrors([]);
      setTimeout(() => props.onCloseModal(true), 1000);
    } else {
      setBrandCreated(false);
      setErrors(result.errors);
    }

    //update list
  }

  const FormField = ({
    id,
    label,
    children,
  }: {
    id: string;
    label?: string;
    children: JSX.Element;
  }): JSX.Element => (
    <div
      className={classNames(
        { 'has-error': !!fieldErrors[id] },
        'create-brand-modal__form-group'
      )}
    >
      <label>
        {label && (
          <span className="create-brand-modal__form-label">{label}</span>
        )}
        {children}
      </label>
      {fieldErrors[id] && <em>{fieldErrors[id].message}</em>}
    </div>
  );

  return (
    <Modal
      title={props.brand ? 'Edit brand' : 'Create brand'}
      onClose={() => props.onCloseModal(brandCreated)}
      isLoading={loading}
      width={600}
      buttons={[
        {
          title: 'Save',
          disabled: creatingBrand || brandCreated,
          onClick: (e: MouseEvent) => {
            saveBrand(e);
          },
        },
      ]}
    >
      <div className="create-brand-modal">
        <form
          className="create-brand-modal__form"
          data-lpignore="true"
          autoComplete="none"
        >
          {/* Store field */}
          <FormField id="accountId" label="Store">
            <select
              name="accountId"
              value={brand.accountId}
              onChange={async event => {
                setBrandField({
                  accountId: Number(event.target.value),
                });
              }}
            >
              <option value={-1} disabled>
                Please select
              </option>
              {storeSettings.map(store => (
                <option value={store.accountId} key={`${store.accountId}`}>
                  {store.name}
                </option>
              ))}
            </select>
          </FormField>

          {/* Name field */}
          <FormField id="name" label="Name">
            <input
              name="name"
              placeholder=""
              type="text"
              value={brand.name}
              data-lpignore="true"
              autoComplete="none"
              onChange={async event => {
                setBrandField({
                  name: event.target.value,
                });
              }}
            />
          </FormField>

          {/* URL field */}
          <FormField id="url" label="URL">
            <input
              name="url"
              placeholder=""
              type="text"
              value={brand.url}
              data-lpignore="true"
              autoComplete="none"
              onChange={async event => {
                setBrandField({ url: event.target.value.replace(/\s/g, '') });
              }}
            />
          </FormField>

          {/* Hidden field */}
          <FormField id="accountId">
            <fieldset>
              <div
                style={{
                  width: 280,
                  display: 'grid',
                  gridTemplateColumns: '30px 1fr',
                }}
              >
                <label style={{ padding: '2px' }} htmlFor="hidden">
                  Hidden
                </label>
                <input
                  id="hidden"
                  type={'checkbox'}
                  checked={isHidden}
                  onChange={() => setIsHidden(!isHidden)}
                />
              </div>
            </fieldset>
          </FormField>

          {/* Categories field */}
          <FormField id="accountId">
            <fieldset>
              <legend>Categories</legend>
              {Object.keys(BrandCategory).map(key => (
                <CheckboxComponent
                  key={key}
                  checked={selectedCategories.has(key as BrandCategory)}
                  label={BrandCategory[key]}
                  handleChange={handleCategoryCheckboxChange}
                  name={key}
                />
              ))}
            </fieldset>
          </FormField>

          <div className="create-brand-modal__form-group-centered">
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <p>Drag'n drop logo here, or click to select manually</p>
            </div>
            {logo && (
              <div className="logoThumbContainer">
                <div className="thumb" key={logo[0].name}>
                  <div className="thumbInner">
                    <img src={logo[0].previewUrl} className="img" alt="logo" />
                  </div>
                </div>
              </div>
            )}
          </div>
          {errors.length > 0 && (
            <div className="u-alert__error">
              {errors.map((error: ErrorMsg, i: number) => {
                return (
                  <div key={`${error}-${i}`}>
                    <div style={{ fontWeight: 500 }}>{error.message}</div>
                  </div>
                );
              })}
            </div>
          )}
          {brandCreated && (
            <div className="u-alert__info">
              <p>
                {props.brand
                  ? 'The brand has been updated successfully.'
                  : 'The brand has been created successfully.'}
              </p>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};

function validateName(brand: Brand): ErrorMsg | null {
  const name = brand.name.trim();
  if (name.length < 1) {
    return { message: 'Please enter name' };
  }

  return null;
}
function validateAccountId(brand: Brand): ErrorMsg | null {
  const accountId = brand.accountId;
  if (accountId === 0) {
    return { message: 'Please enter accountId' };
  }

  return null;
}

function validateUrl(brand: Brand): ErrorMsg | null {
  const url = brand.url.trim();
  if (url.length < 1) {
    return { message: 'Please enter url' };
  }

  return null;
}
